import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import api from '../api';

export default function Devices() {
  const { tenantId, saasId, paasId } = useParams();
  const { user } = useSelector(state => state.auth);
  const [activeTab, setActiveTab] = useState('devices');
  const [devices, setDevices] = useState([]);
  const [apps, setApps] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAppModal, setShowAppModal] = useState(false);
  const [stats, setStats] = useState(null);

  const ownerId = tenantId || saasId || paasId || user?._id;
  const ownerType = tenantId ? 'tenant' : saasId ? 'saas' : paasId ? 'paas' : 'user';

  useEffect(() => {
    loadDevices();
    loadApps();
    loadAnalytics();
  }, [ownerId, ownerType]);

  // Polling for real-time data updates
  useEffect(() => {
    const pollDevices = setInterval(() => {
      loadDevices();
    }, 15000); // 15 seconds

    const pollApps = setInterval(() => {
      loadApps();
    }, 15000); // 15 seconds

    const pollAnalytics = setInterval(() => {
      loadAnalytics();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(pollDevices);
      clearInterval(pollApps);
      clearInterval(pollAnalytics);
    };
  }, [ownerId, ownerType]);

  const loadDevices = async () => {
    try {
      const res = await api.get(`/devices/owner/${ownerType}/${ownerId}`);
      setDevices(res.data.data.devices || []);
      setStats(res.data.data);
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApps = async () => {
    try {
      const res = await api.get(`/client-apps/owner/${ownerType}/${ownerId}`);
      setApps(res.data.data.apps || []);
    } catch (error) {
      console.error('Failed to load apps:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const res = await api.get(`/device-analytics/summary/${ownerId}`);
      setAnalytics(res.data.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const handleCreateApp = async (appData) => {
    try {
      await api.post('/client-apps', { ...appData, ownerId, ownerType, tenantId, saasId, paasId });
      setShowAppModal(false);
      loadApps();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create app');
    }
  };

  const handleUpdateBranding = async (appId, branding) => {
    try {
      await api.patch(`/client-apps/${appId}/branding`, branding);
      loadApps();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update branding');
    }
  };

  const handleRequestBuild = async (appId, platform) => {
    try {
      await api.post(`/client-apps/${appId}/build`, { platform });
      alert(`${platform} build requested. Build will complete shortly.`);
    } catch (error) {
      alert(error.response?.data?.message || 'Build request failed');
    }
  };

  const handleSuspendDevice = async (deviceId) => {
    try {
      await api.patch(`/devices/${deviceId}/suspend`);
      loadDevices();
    } catch (error) {
      alert('Failed to suspend device');
    }
  };

  const handleActivateDevice = async (deviceId) => {
    try {
      await api.patch(`/devices/${deviceId}/activate`);
      loadDevices();
    } catch (error) {
      alert('Failed to activate device');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'devices', label: 'Devices' },
    { id: 'apps', label: 'Client Apps' },
    { id: 'analytics', label: 'Analytics' }
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Device Management</h1>
        {activeTab === 'apps' && (
          <button onClick={() => setShowAppModal(true)} className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600">
            + Create App
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm text-gray-500">Total Devices</p>
          <p className="text-2xl font-bold">{stats?.total || 0}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">{stats?.active || 0}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm text-gray-500">Client Apps</p>
          <p className="text-2xl font-bold">{apps.length}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm text-gray-500">Total Events</p>
          <p className="text-2xl font-bold">{analytics?.totalEvents || 0}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b">
          <nav className="flex space-x-6 px-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'devices' && (
            <div>
              {devices.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No devices registered</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b">
                      <th className="pb-3">Device</th>
                      <th className="pb-3">Type</th>
                      <th className="pb-3">Platform</th>
                      <th className="pb-3">Status</th>
                      <th className="pb-3">Last Seen</th>
                      <th className="pb-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map(device => (
                      <tr key={device._id} className="border-b">
                        <td className="py-3">
                          <div>
                            <p className="font-medium">{device.name || 'Unknown Device'}</p>
                            <p className="text-xs text-gray-400 font-mono">{device.deviceId?.substring(0, 12)}...</p>
                          </div>
                        </td>
                        <td className="py-3 text-sm capitalize">{device.type}</td>
                        <td className="py-3 text-sm capitalize">{device.platform}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            device.status === 'active' ? 'bg-green-100 text-green-700' :
                            device.status === 'suspended' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>{device.status}</span>
                        </td>
                        <td className="py-3 text-sm text-gray-500">
                          {device.lastSeen ? new Date(device.lastSeen).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="py-3">
                          {device.status === 'active' ? (
                            <button onClick={() => handleSuspendDevice(device.deviceId)} className="text-xs text-red-600 hover:underline">
                              Suspend
                            </button>
                          ) : (
                            <button onClick={() => handleActivateDevice(device.deviceId)} className="text-xs text-green-600 hover:underline">
                              Activate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'apps' && (
            <div>
              {apps.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 mb-4">No client apps created yet</p>
                  <button onClick={() => setShowAppModal(true)} className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600">
                    Create Your First App
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {apps.map(app => (
                    <div key={app._id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{app.name}</h3>
                          <p className="text-xs text-gray-400">{app.slug}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          app.status === 'published' ? 'bg-green-100 text-green-700' :
                          app.status === 'draft' ? 'bg-gray-100 text-gray-700' : 'bg-gray-100'
                        }`}>{app.status}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {app.platform === 'android' && <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Android</span>}
                        {app.platform === 'ios' && <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">iOS</span>}
                        {app.platform === 'web' && <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Web</span>}
                        {app.platform === 'both' && (
                          <>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Android</span>
                            <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">iOS</span>
                          </>
                        )}
                      </div>
                      <div className="mt-4 flex gap-2">
                        <button onClick={() => handleRequestBuild(app._id, 'web')} className="px-3 py-1 text-xs bg-primary-500 text-white rounded hover:bg-primary-600">
                          Build Web
                        </button>
                        <button onClick={() => handleRequestBuild(app._id, 'android')} className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">
                          Build APK
                        </button>
                        <button onClick={() => handleRequestBuild(app._id, 'ios')} className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600">
                          Build IPA
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && analytics && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(analytics.byType || {}).map(([type, count]) => (
                  <div key={type} className="bg-gray-50 rounded p-4">
                    <p className="text-sm text-gray-500 capitalize">{type.replace('_', ' ')}</p>
                    <p className="text-xl font-bold">{count}</p>
                  </div>
                ))}
              </div>
              <div>
                <h3 className="font-semibold mb-3">Events by Type</h3>
                <div className="space-y-2">
                  {Object.entries(analytics.byType || {}).map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center">
                      <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-gray-200 rounded overflow-hidden">
                          <div
                            className="h-full bg-primary-500"
                            style={{ width: `${(count / analytics.totalEvents) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showAppModal && (
        <AppModal
          onClose={() => setShowAppModal(false)}
          onSubmit={handleCreateApp}
          onUpdateBranding={handleUpdateBranding}
        />
      )}
    </div>
  );
}

function AppModal({ onClose, onSubmit, app, onUpdateBranding }) {
  const [name, setName] = useState(app?.name || '');
  const [platform, setPlatform] = useState(app?.platform || 'web');
  const [packageName, setPackageName] = useState(app?.packageName || '');
  const [primaryColor, setPrimaryColor] = useState(app?.branding?.primaryColor || '#4a90d9');
  const [accentColor, setAccentColor] = useState(app?.branding?.accentColor || '#22c55e');
  const [apiBaseUrl, setApiBaseUrl] = useState(app?.configuration?.apiBaseUrl || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({
        name,
        platform,
        packageName,
        version: { current: '1.0.0', minSupported: '1.0.0' },
        branding: { primaryColor, accentColor },
        configuration: { apiBaseUrl }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg font-semibold">{app ? 'Edit App' : 'Create Client App'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">App Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="My Client App"
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="web">Web Only</option>
              <option value="android">Android</option>
              <option value="ios">iOS</option>
              <option value="both">Android + iOS</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Package Name</label>
            <input
              type="text"
              value={packageName}
              onChange={(e) => setPackageName(e.target.value)}
              placeholder="com.client.app"
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Base URL</label>
            <input
              type="url"
              value={apiBaseUrl}
              onChange={(e) => setApiBaseUrl(e.target.value)}
              placeholder="https://api.example.com"
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
              <div className="flex gap-2">
                <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-10 h-10 border rounded cursor-pointer" />
                <input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="flex-1 px-2 border rounded" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Accent Color</label>
              <div className="flex gap-2">
                <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="w-10 h-10 border rounded cursor-pointer" />
                <input type="text" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="flex-1 px-2 border rounded" />
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border text-gray-700 rounded hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 disabled:opacity-50">
              {loading ? 'Creating...' : app ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}